import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Typography,
  Row,
  Col,
  Form,
  Select,
  Button,
  Descriptions,
  Modal,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import Layouts from '../../../components/Layouts';
import { getDirectusClient } from '../../../lib/directus';
import { readItem, readItems, updateItem } from '@directus/sdk';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;
const { Option } = Select;

const formatNumber = number => {
  if (!number) return '0.00';
  return Number(number).toLocaleString('es-CO', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

function OrderAssign() {
  const { order_id } = useParams();
  const [order, setOrder] = useState({});
  const [bodegas, setBodegas] = useState([]);
  const navigate = useNavigate();
  const userRoleID = useSelector(state => state.userdata.role?.name);
  const userBodega = useSelector(state => state.userdata.bodega?.id);

  useEffect(() => {
    const getOrder = async () => {
      const directus = await getDirectusClient();
      // Solo pedimos los campos necesarios
      await directus
        .request(
          readItem('ordenes_decompra', order_id, {
            fields: [
              'id',
              'numero_factura',
              'proveedor.nombre',
              'date_created',
              'precio_total',
              'productos.id',
              'productos.precio_uni_final',
              'productos.cantidad',
              'productos.producto_detalle.nombre',
            ],
          })
        )
        .then(res => {
          setOrder(res);
        });
    };

    const getBodegas = async () => {
      const directus = await getDirectusClient();
      await directus
        .request(
          readItems('bodegas', {
            fields: ['id', 'nombre'],
            filter: {
              ...(userRoleID !== 'adminapp' && userRoleID !== 'Administrator'
                ? {
                    id: { _eq: userBodega },
                  }
                : {}),
            },
          })
        )
        .then(res => {
          setBodegas(res);
        });
    };
    getOrder();
    getBodegas();
    // eslint-disable-next-line
  }, []);

  const onFinish = async values => {
    if (!values.bodega) {
      Modal.error({
        content: 'Debe seleccionar una bodega.',
      });
      return;
    }
    const directus = await getDirectusClient();
    await directus
      .request(
        updateItem('ordenes_decompra', order_id, {
          bodegas: {
            create: [
              {
                ordenes_decompra_id: order_id,
                bodegas_id: {
                  id: values.bodega,
                },
              },
            ],
            update: [],
            delete: [],
          },
        })
      )
      .then(res => {
        Modal.success({
          content: 'Orden Asignada correctamente',
          onOk() {
            navigate('/orders');
          },
        });
      })
      .catch(err => {
        Modal.error({
          content: 'Error al asignar la orden. Intente nuevamente.',
        });
        console.log(err);
      });
  };

  const onFinishFailed = errorInfo => {
    Modal.error({
      content: 'Por favor complete correctamente el formulario.',
    });
    console.log('Failed:', errorInfo);
  };

  return (
    <Layouts>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
        <Title level={2}>Detalles de la Orden</Title>

        <Card style={{ marginTop: 24 }}>
          <Descriptions title={`Orden ${order.id || ''}`} bordered>
            <Descriptions.Item label="Número de Orden" span={2}>
              {order.id}
            </Descriptions.Item>
            <Descriptions.Item label="Factura" span={2}>
              {order.numero_factura}
            </Descriptions.Item>
            <Descriptions.Item label="Proveedor" span={2}>
              {order.proveedor?.nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de Creación" span={2}>
              {order.date_created
                ? moment(order.date_created).format('DD/MM/YYYY HH:mm')
                : ''}
            </Descriptions.Item>
          </Descriptions>

          {order.productos && order.productos.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Title level={4}>Productos</Title>
              <div style={{ marginBottom: 24 }}>
                {order.productos.map((producto, index) => (
                  <Row
                    key={producto.id || index}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                    align="middle"
                    justify="space-between"
                  >
                    <Col span={12}>
                      <Text>{producto?.producto_detalle?.nombre}</Text>
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}>
                      <Text type="secondary">
                        Precio: ${formatNumber(producto?.precio_uni_final) || 0}
                      </Text>
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}>
                      <Text>Cantidad: {producto?.cantidad || 0}</Text>
                    </Col>
                  </Row>
                ))}
              </div>
              <div
                style={{
                  borderTop: '2px solid #1890ff',
                  paddingTop: 16,
                  textAlign: 'right',
                }}
              >
                <Title level={5}>
                  Total de la Orden: ${formatNumber(order.precio_total) || 0}
                </Title>
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: 24,
              borderTop: '1px solid #f0f0f0',
              paddingTop: 24,
            }}
          >
            <Title level={4}>Asignar Bodega</Title>
            <Form
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              style={{ maxWidth: 400 }}
            >
              <Form.Item
                name="bodega"
                label="Seleccionar Bodega"
                rules={[
                  {
                    required: true,
                    message: 'Por favor seleccione una bodega',
                  },
                ]}
              >
                <Select placeholder="Seleccione una bodega" allowClear>
                  {bodegas.map(bodega => (
                    <Option key={bodega.id} value={bodega.id}>
                      {bodega.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                >
                  Guardar
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Card>
      </div>
    </Layouts>
  );
}

export default OrderAssign;
